/**
 * Single source of truth for all role constants in the application.
 *
 * The `role_name` values come directly from the backend roles API and should
 * never be changed without a corresponding backend migration.
 *
 * Backend roles endpoint returns:
 *   [{ role_id: 12, role_name: "lyt_admin" }, ...]
 *
 * Legacy `usertype` values (e.g. "LYT_Admin") are still stored in
 * `state.user.personalInformation.usertype` and used by the permissions
 * matrix.  The LEGACY_ROLE_VALUES and mapping helpers bridge the two worlds.
 */

// ---------------------------------------------------------------------------
// Backend role_name values  (source of truth)
// ---------------------------------------------------------------------------

export const ROLE_NAMES = Object.freeze({
    // LYT internal
    LYT_ADMIN: 'lyt_admin',
    LYT_EMPLOYEE: 'lyt_employee',

    // Transit agency
    TRANSIT_AGENCY_OWNER: 'transit_agency_owner',
    TRANSIT_AGENCY_ADMIN: 'transit_agency_admin',
    TRANSIT_AGENCY_VIEWER: 'transit_agency_viewer',

    // Emergency agency
    EMERGENCY_AGENCY_OWNER: 'emergency_agency_owner',
    EMERGENCY_AGENCY_ADMIN: 'emergency_agency_admin',
    EMERGENCY_AGENCY_VIEWER: 'emergency_agency_viewer',

    // Traffic viewer
    TRAFFIC_VIEWER: 'traffic_viewer',
});

// ---------------------------------------------------------------------------
// Agency types  (from the agency data — agency_type field)
// ---------------------------------------------------------------------------

export const AGENCY_TYPES = Object.freeze({
    TRANSIT: 'transit',
    EMERGENCY: 'emergency',
    SNOW: 'snow',
});

// ---------------------------------------------------------------------------
// Legacy usertype values (stored in state.user.personalInformation.usertype)
// ---------------------------------------------------------------------------

export const LEGACY_ROLE_VALUES = Object.freeze({
    LYT_ADMIN: 'LYT_Admin',
    LYT_EMPLOYEE: 'LYT_Employee',
    AGENCY_OWNER: 'Agency_Owner',
    AGENCY_ADMIN: 'Agency_Admin',
    AGENCY_VIEWER: 'Agency_Viewer',
});

// ---------------------------------------------------------------------------
// Role groups  (arrays of ROLE_NAMES values)
// ---------------------------------------------------------------------------

export const ROLE_GROUPS = Object.freeze({
    /** LYT-internal users who can see all agencies */
    LYT_INTERNAL: [
        ROLE_NAMES.LYT_ADMIN,
        ROLE_NAMES.LYT_EMPLOYEE,
    ],

    /** Transit-agency users (single agency, auto-detected) */
    TRANSIT_AGENCY: [
        ROLE_NAMES.TRANSIT_AGENCY_OWNER,
        ROLE_NAMES.TRANSIT_AGENCY_ADMIN,
        ROLE_NAMES.TRANSIT_AGENCY_VIEWER,
    ],

    /** Emergency-agency users (single agency, auto-detected) */
    EMERGENCY_AGENCY: [
        ROLE_NAMES.EMERGENCY_AGENCY_OWNER,
        ROLE_NAMES.EMERGENCY_AGENCY_ADMIN,
        ROLE_NAMES.EMERGENCY_AGENCY_VIEWER,
    ],

    /** Traffic viewers (city/jurisdiction scoped) */
    TRAFFIC_VIEWER: [
        ROLE_NAMES.TRAFFIC_VIEWER,
    ],
});

/** All agency roles (transit + emergency) */
export const ALL_AGENCY_ROLES = [
    ...ROLE_GROUPS.TRANSIT_AGENCY,
    ...ROLE_GROUPS.EMERGENCY_AGENCY,
];

/**
 * LYT Team Management dropdown order: Admin → User (`lyt_admin` → `lyt_employee`).
 * UI labels are shortened via {@link formatLytTeamRole} in role-utils.js.
 */
export const LYT_ROLE_DISPLAY_ORDER = Object.freeze([
    ROLE_NAMES.LYT_ADMIN,
    ROLE_NAMES.LYT_EMPLOYEE,
]);

/**
 * Agency role dropdown order: Owner → Admin → Viewer → Traffic Viewer.
 * @param {string} agencyType - {@link AGENCY_TYPES} value
 * @returns {readonly string[]}
 */
export const getAgencyRoleDisplayOrder = (agencyType) =>
    Object.freeze(
        agencyType === AGENCY_TYPES.EMERGENCY
            ? [...ROLE_GROUPS.EMERGENCY_AGENCY, ROLE_NAMES.TRAFFIC_VIEWER]
            : [...ROLE_GROUPS.TRANSIT_AGENCY, ROLE_NAMES.TRAFFIC_VIEWER]
    );

/**
 * Roles an agency owner/admin may assign (same order as {@link getAgencyRoleDisplayOrder}).
 */
export const getAgencyOwnerAdminAssignableRoleNames = (agencyType) =>
    getAgencyRoleDisplayOrder(agencyType);

/**
 * Roles an agency viewer may assign: Viewer, then Traffic Viewer.
 */
export const getAgencyViewerAssignableRoleNames = (agencyType) => {
    const viewer = agencyType === AGENCY_TYPES.EMERGENCY
        ? ROLE_NAMES.EMERGENCY_AGENCY_VIEWER
        : ROLE_NAMES.TRANSIT_AGENCY_VIEWER;
    return [viewer, ROLE_NAMES.TRAFFIC_VIEWER];
};

/** Default order when showing all agency + LYT roles (legacy fallback). */
export const ALL_ASSIGNABLE_ROLE_DISPLAY_ORDER = Object.freeze([
    ...ROLE_GROUPS.TRANSIT_AGENCY,
    ...ROLE_GROUPS.EMERGENCY_AGENCY,
    ROLE_NAMES.TRAFFIC_VIEWER,
    ...LYT_ROLE_DISPLAY_ORDER,
]);

/**
 * Sort API role rows (or role_name strings) to match a display-order list.
 * @param {Array} roles
 * @param {string[]} orderedRoleNames
 * @param {string} [roleNameKey='role_name']
 */
export const sortRolesByDisplayOrder = (
    roles,
    orderedRoleNames,
    roleNameKey = 'role_name'
) => {
    const rank = new Map(orderedRoleNames.map((name, index) => [name, index]));
    return [...roles].sort((a, b) => {
        const aName = typeof a === 'string' ? a : a[roleNameKey];
        const bName = typeof b === 'string' ? b : b[roleNameKey];
        return (rank.get(aName) ?? Number.MAX_SAFE_INTEGER)
            - (rank.get(bName) ?? Number.MAX_SAFE_INTEGER);
    });
};

/** Every known role — use for routes accessible to all authenticated users. */
export const ALL_AUTHENTICATED_ROLES = Object.freeze([
    ...ROLE_GROUPS.LYT_INTERNAL,
    ...ROLE_GROUPS.TRANSIT_AGENCY,
    ...ROLE_GROUPS.EMERGENCY_AGENCY,
    ...ROLE_GROUPS.TRAFFIC_VIEWER,
]);

/**
 * Roles that can manage users / access admin-only pages (e.g. Requests).
 * lyt_admin, agency owners, and agency admins.
 */
export const ADMIN_ROLES = Object.freeze([
    ROLE_NAMES.LYT_ADMIN,
    ROLE_NAMES.LYT_EMPLOYEE,
    ROLE_NAMES.TRANSIT_AGENCY_OWNER,
    ROLE_NAMES.TRANSIT_AGENCY_ADMIN,
    ROLE_NAMES.EMERGENCY_AGENCY_OWNER,
    ROLE_NAMES.EMERGENCY_AGENCY_ADMIN,
]);

// ---------------------------------------------------------------------------
// Legacy ↔ New mapping tables
// ---------------------------------------------------------------------------

/**
 * Map from legacy usertype values → backend role_name values.
 * Covers the five legacy roles that existed before the backend migration.
 */
export const LEGACY_TO_ROLE_NAME = Object.freeze({
    [LEGACY_ROLE_VALUES.LYT_ADMIN]: ROLE_NAMES.LYT_ADMIN,
    [LEGACY_ROLE_VALUES.LYT_EMPLOYEE]: ROLE_NAMES.LYT_EMPLOYEE,
    [LEGACY_ROLE_VALUES.AGENCY_OWNER]: ROLE_NAMES.TRANSIT_AGENCY_OWNER,
    [LEGACY_ROLE_VALUES.AGENCY_ADMIN]: ROLE_NAMES.TRANSIT_AGENCY_ADMIN,
    [LEGACY_ROLE_VALUES.AGENCY_VIEWER]: ROLE_NAMES.TRANSIT_AGENCY_VIEWER,
});

/**
 * Map from backend role_name values → legacy usertype values.
 * Only roles that have a legacy equivalent are included.
 */
export const ROLE_NAME_TO_LEGACY = Object.freeze({
    [ROLE_NAMES.LYT_ADMIN]: LEGACY_ROLE_VALUES.LYT_ADMIN,
    [ROLE_NAMES.LYT_EMPLOYEE]: LEGACY_ROLE_VALUES.LYT_EMPLOYEE,
    [ROLE_NAMES.TRANSIT_AGENCY_OWNER]: LEGACY_ROLE_VALUES.AGENCY_OWNER,
    [ROLE_NAMES.TRANSIT_AGENCY_ADMIN]: LEGACY_ROLE_VALUES.AGENCY_ADMIN,
    [ROLE_NAMES.TRANSIT_AGENCY_VIEWER]: LEGACY_ROLE_VALUES.AGENCY_VIEWER,
    [ROLE_NAMES.EMERGENCY_AGENCY_OWNER]: LEGACY_ROLE_VALUES.AGENCY_OWNER,
    [ROLE_NAMES.EMERGENCY_AGENCY_ADMIN]: LEGACY_ROLE_VALUES.AGENCY_ADMIN,
    [ROLE_NAMES.EMERGENCY_AGENCY_VIEWER]: LEGACY_ROLE_VALUES.AGENCY_VIEWER,
});

// ---------------------------------------------------------------------------
// Display labels  (human-readable names for UI)
// ---------------------------------------------------------------------------

export const ROLE_LABELS = Object.freeze({
    [ROLE_NAMES.LYT_ADMIN]: 'LYT Admin',
    [ROLE_NAMES.LYT_EMPLOYEE]: 'LYT Employee',
    [ROLE_NAMES.TRANSIT_AGENCY_OWNER]: 'Transit Agency Owner',
    [ROLE_NAMES.TRANSIT_AGENCY_ADMIN]: 'Transit Agency Admin',
    [ROLE_NAMES.TRANSIT_AGENCY_VIEWER]: 'Transit Agency Viewer',
    [ROLE_NAMES.EMERGENCY_AGENCY_OWNER]: 'Emergency Agency Owner',
    [ROLE_NAMES.EMERGENCY_AGENCY_ADMIN]: 'Emergency Agency Admin',
    [ROLE_NAMES.EMERGENCY_AGENCY_VIEWER]: 'Emergency Agency Viewer',
    [ROLE_NAMES.TRAFFIC_VIEWER]: 'Traffic Viewer',
});

// ---------------------------------------------------------------------------
// Role-check helpers
//
// These accept either the **new** role_name or the **legacy** usertype value
// so callers do not need to convert first.
// ---------------------------------------------------------------------------

/**
 * Normalise any role value (legacy or new) to the backend role_name.
 * Returns the input unchanged if it's already a backend role_name or unknown.
 */
export const normalizeRoleName = (role) => {
    if (!role) return role;
    // If it's a legacy value, convert first
    return LEGACY_TO_ROLE_NAME[role] || role;
};

/** Returns true when the role is an LYT-internal role (lyt_admin | lyt_employee). */
export const isLytRole = (role) => {
    const normalized = normalizeRoleName(role);
    return ROLE_GROUPS.LYT_INTERNAL.includes(normalized);
};

/** Returns true when the role is any agency role (transit or emergency). */
export const isAgencyRole = (role) => {
    const normalized = normalizeRoleName(role);
    return ALL_AGENCY_ROLES.includes(normalized);
};

/** Returns true when the role is a transit agency role. */
export const isTransitAgencyRole = (role) => {
    const normalized = normalizeRoleName(role);
    return ROLE_GROUPS.TRANSIT_AGENCY.includes(normalized);
};

/** Returns true when the role is an emergency agency role. */
export const isEmergencyAgencyRole = (role) => {
    const normalized = normalizeRoleName(role);
    return ROLE_GROUPS.EMERGENCY_AGENCY.includes(normalized);
};

/** Returns true when the role is a traffic viewer. */
export const isTrafficViewerRole = (role) => {
    const normalized = normalizeRoleName(role);
    return ROLE_GROUPS.TRAFFIC_VIEWER.includes(normalized);
};

/**
 * Map-specific role group constants for determining map behaviour.
 * - LYT_INTERNAL: Sidebar agency selector → load all cities for selected agency
 * - AGENCY_USER: Auto-detect user's agency → load all cities automatically
 * - TRAFFIC_VIEWER: Sidebar jurisdiction selector → load single city
 */
export const MAP_ROLE_GROUPS = Object.freeze({
    LYT_INTERNAL: 'LYT_INTERNAL',
    AGENCY_USER: 'AGENCY_USER',
    TRAFFIC_VIEWER: 'TRAFFIC_VIEWER',
    UNKNOWN: 'UNKNOWN',
});

/**
 * Determine which map behaviour group a user's role falls into.
 * @param {string} role - The user's role (backend role_name or legacy usertype)
 * @returns {string} One of MAP_ROLE_GROUPS values
 */
export const getUserMapRoleGroup = (role) => {
    const normalized = normalizeRoleName(role);
    if (ROLE_GROUPS.LYT_INTERNAL.includes(normalized)) return MAP_ROLE_GROUPS.LYT_INTERNAL;
    if ([...ROLE_GROUPS.TRANSIT_AGENCY, ...ROLE_GROUPS.EMERGENCY_AGENCY].includes(normalized)) return MAP_ROLE_GROUPS.AGENCY_USER;
    if (ROLE_GROUPS.TRAFFIC_VIEWER.includes(normalized)) return MAP_ROLE_GROUPS.TRAFFIC_VIEWER;
    return MAP_ROLE_GROUPS.UNKNOWN;
};

/** Returns true when the role is lyt_admin (accepts legacy 'LYT_Admin' too). */
export const isLytAdminRole = (role) => {
    const normalized = normalizeRoleName(role);
    return normalized === ROLE_NAMES.LYT_ADMIN;
};

/**
 * Returns true when the role is any kind of admin
 * (lyt_admin, transit_agency_admin, transit_agency_owner,
 *  emergency_agency_admin, emergency_agency_owner).
 */
export const isAdminRole = (role) => {
    const normalized = normalizeRoleName(role);
    return [
        ROLE_NAMES.LYT_ADMIN,
        ROLE_NAMES.TRANSIT_AGENCY_OWNER,
        ROLE_NAMES.TRANSIT_AGENCY_ADMIN,
        ROLE_NAMES.EMERGENCY_AGENCY_OWNER,
        ROLE_NAMES.EMERGENCY_AGENCY_ADMIN,
    ].includes(normalized);
};
